import { useMemo, useState } from 'react';
import SelectField from '../Forms/SelectField';
import { getPlayerAvatar } from '../../utils/player';
import {
  EVENT_TEMPLATES,
  MAX_TIE_BREAKERS,
  MODE_OPTIONS,
  eventToForm,
  findTemplate,
  formToEventFields,
  getCriteriaGroups,
  getEmptyEventForm,
  modesToOption,
  validateEventForm,
} from '../../utils/eventsView';

const BOTH_MODES = MODE_OPTIONS.find((o) => o.value === 'both').modes;

function CriterionSelect({ id, value, groups, exclude = [], onChange, label }) {
  return (
    <SelectField id={id} value={value} onChange={onChange} label={label}>
      {groups.map(({ group, criteria }) => (
        <optgroup key={group} label={group}>
          {criteria
            .filter((c) => c.id === value || !exclude.includes(c.id))
            .map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
        </optgroup>
      ))}
    </SelectField>
  );
}

function PresetChip({ icon, name, blurb, selected, onClick }) {
  return (
    <button
      type="button"
      className={`event-preset ${selected ? 'is-selected' : ''}`}
      aria-pressed={selected}
      onClick={onClick}
    >
      <span className="event-preset__badge" aria-hidden="true">
        <span className="material-symbols-rounded">{icon}</span>
      </span>
      <span className="event-preset__text">
        <span className="event-preset__name">{name}</span>
        <span className="event-preset__blurb">{blurb}</span>
      </span>
    </button>
  );
}

// Create / edit an event (#50, Figma 791:18510 new, 791:18973 edit). Editing is opened
// from the settings cog on an event and carries End event and Save.
const EventModal = ({
  mode = 'create',
  event = null,
  allPlayers = [],
  onClose,
  onSave,
  onEnd,
  onDelete,
}) => {
  const isEdit = mode === 'edit';
  const isEnded = event?.status === 'ended';
  const [form, setForm] = useState(() => (isEdit && event ? eventToForm(event) : getEmptyEventForm()));
  // A new event starts on Custom (as designed); an existing one shows the preset it matches
  const [templateId, setTemplateId] = useState(() => (isEdit ? findTemplate(form.criterion, form.tieBreakers)?.id || null : null));
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const groups = useMemo(() => getCriteriaGroups(form.modes), [form.modes]);
  const allowedIds = useMemo(() => groups.flatMap((g) => g.criteria.map((c) => c.id)), [groups]);
  const errors = validateEventForm(form);

  const playerNames = useMemo(
    () => (allPlayers || []).map((p) => (typeof p === 'object' ? p.name : p)).filter(Boolean)
      .sort((a, b) => a.localeCompare(b)),
    [allPlayers]
  );

  const applyTemplate = (template) => {
    setTemplateId(template.id);
    // A preset the current modes cannot score (Real MVP in classic only) opens up both modes
    const modes = allowedIds.includes(template.criterion) ? form.modes : BOTH_MODES;
    update({
      name: form.name.trim() ? form.name : template.name,
      modes,
      criterion: template.criterion,
      tieBreakers: template.tieBreakers,
    });
  };

  const setModes = (option) => {
    const modes = MODE_OPTIONS.find((o) => o.value === option).modes;
    const allowed = getCriteriaGroups(modes).flatMap((g) => g.criteria.map((c) => c.id));
    // Drop criteria the new modes can never score (e.g. MVPs in a classic-only event)
    const criterion = allowed.includes(form.criterion) ? form.criterion : 'matchWins';
    const tieBreakers = form.tieBreakers.filter((tb) => allowed.includes(tb) && tb !== criterion);
    setTemplateId(findTemplate(criterion, tieBreakers)?.id || null);
    update({ modes, criterion, tieBreakers });
  };

  const setCriterion = (stat) => {
    setTemplateId(null);
    update({ criterion: stat, tieBreakers: form.tieBreakers.filter((tb) => tb !== stat) });
  };

  const setTieBreaker = (index, stat) => {
    setTemplateId(null);
    const next = [...form.tieBreakers];
    next[index] = stat;
    update({ tieBreakers: next });
  };

  const removeTieBreaker = (index) => {
    setTemplateId(null);
    update({ tieBreakers: form.tieBreakers.filter((_, i) => i !== index) });
  };

  const addTieBreaker = () => {
    const used = [form.criterion, ...form.tieBreakers];
    const next = allowedIds.find((id) => !used.includes(id));
    if (!next) return;
    setTemplateId(null);
    update({ tieBreakers: [...form.tieBreakers, next] });
  };

  const togglePlayer = (name) => {
    const current = Array.isArray(form.participants) ? form.participants : [];
    update({
      participants: current.includes(name) ? current.filter((n) => n !== name) : [...current, name],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (errors.length > 0) {
      setShowErrors(true);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(formToEventFields(form));
    } finally {
      setIsSaving(false);
    }
  };

  const isPicking = Array.isArray(form.participants);
  const modeOption = modesToOption(form.modes);

  return (
    <div className="modal-overlay">
      <div className="modal event-form" role="dialog" aria-modal="true" aria-labelledby="event-form-title">
        <div className="event-form__header">
          <span className="event-page__icon-btn" aria-hidden="true" />
          <h3 id="event-form-title" className="event-form__title">{isEdit ? 'Edit Event' : 'New Event'}</h3>
          <button type="button" className="event-page__icon-btn" onClick={onClose} aria-label="Close">
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
        </div>

        <form className="event-form__body" onSubmit={handleSubmit} noValidate>
          <label htmlFor="event-name" className="event-form__label">Event name</label>
          <input
            id="event-name"
            type="text"
            className="input"
            value={form.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="e.g. Autumn League"
            maxLength={60}
          />

          <div className="event-form__row">
            <label htmlFor="event-start" className="event-form__label">Start date</label>
            <label htmlFor="event-end" className="event-form__label">End date (optional)</label>
          </div>
          <div className="event-form__row event-form__row--fields">
            <input
              id="event-start"
              type="date"
              className="input"
              value={form.startDate}
              onChange={(e) => update({ startDate: e.target.value })}
            />
            <input
              id="event-end"
              type="date"
              className="input"
              value={form.endDate}
              min={form.startDate || undefined}
              onChange={(e) => update({ endDate: e.target.value })}
            />
          </div>
          {Array.isArray(event?.sessionIds) && (
            <p className="event-form__hint">
              This event keeps the matches it was created with, so changing dates does not change its leaderboard.
            </p>
          )}

          <p className="event-form__label" id="event-modes-label">Game modes</p>
          <div className="event-pills" role="group" aria-labelledby="event-modes-label">
            {MODE_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`event-pill ${modeOption === o.value ? 'is-selected' : ''}`}
                aria-pressed={modeOption === o.value}
                onClick={() => setModes(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>

          <p className="event-form__label" id="event-players-label">Players</p>
          <div className="event-pills" role="group" aria-labelledby="event-players-label">
            <button
              type="button"
              className={`event-pill ${!isPicking ? 'is-selected' : ''}`}
              aria-pressed={!isPicking}
              onClick={() => update({ participants: 'all' })}
            >
              All players
            </button>
            <button
              type="button"
              className={`event-pill ${isPicking ? 'is-selected' : ''}`}
              aria-pressed={isPicking}
              onClick={() => update({ participants: isPicking ? form.participants : [] })}
            >
              Pick players
            </button>
          </div>
          {isPicking && (
            <>
              <p className="event-form__hint">{form.participants.length} selected</p>
              <div className="event-player-grid">
                {playerNames.map((name) => {
                  const selected = form.participants.includes(name);
                  return (
                    <button
                      key={name}
                      type="button"
                      className={`match-setup-player ${selected ? 'is-selected' : ''}`}
                      aria-pressed={selected}
                      onClick={() => togglePlayer(name)}
                    >
                      <img src={getPlayerAvatar(allPlayers, name)} alt="" className="avatar match-setup-avatar" />
                      <span className="match-setup-name">{name}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <p className="event-form__label" id="event-type-label">Event type</p>
          <div className="event-presets" role="group" aria-labelledby="event-type-label">
            <PresetChip
              icon="tune"
              name="Custom"
              blurb="Make your own"
              selected={!templateId}
              onClick={() => setTemplateId(null)}
            />
            {EVENT_TEMPLATES.map((t) => (
              <PresetChip
                key={t.id}
                icon={t.icon}
                name={t.name}
                blurb={t.blurb}
                selected={templateId === t.id}
                onClick={() => applyTemplate(t)}
              />
            ))}
          </div>

          <label htmlFor="event-criterion" className="event-form__label">Winning criterion</label>
          <CriterionSelect
            id="event-criterion"
            value={form.criterion}
            groups={groups}
            onChange={setCriterion}
            label="Winning criterion"
          />

          <p className="event-form__label">Tie-breakers</p>
          {form.tieBreakers.map((stat, i) => (
            <div key={i} className="event-tiebreak">
              <span className="event-tiebreak__n" aria-hidden="true">{i + 1}</span>
              <CriterionSelect
                id={`event-tb-${i}`}
                value={stat}
                groups={groups}
                exclude={[form.criterion, ...form.tieBreakers.filter((_, j) => j !== i)]}
                onChange={(v) => setTieBreaker(i, v)}
                label={`Tie-breaker ${i + 1}`}
              />
              <button
                type="button"
                className="event-page__icon-btn"
                onClick={() => removeTieBreaker(i)}
                aria-label={`Remove tie-breaker ${i + 1}`}
              >
                <span className="material-symbols-rounded" aria-hidden="true">delete</span>
              </button>
            </div>
          ))}
          {form.tieBreakers.length < MAX_TIE_BREAKERS && (
            <button type="button" className="event-add" onClick={addTieBreaker}>
              <span className="material-symbols-rounded" aria-hidden="true">add</span>
              Add tie-breaker
            </button>
          )}

          {showErrors && errors.length > 0 && (
            <ul className="event-form__errors" role="alert">
              {errors.map((err) => <li key={err}>{err}</li>)}
            </ul>
          )}

          {isEdit ? (
            <>
              <div className="event-form__footer">
                {!isEnded && onEnd && (
                  <button type="button" className="btn btn-danger" onClick={() => onEnd(event)}>
                    <span className="btn-text">END EVENT</span>
                  </button>
                )}
                <button type="submit" className="btn btn-secondary" disabled={isSaving}>
                  <span className="btn-text">SAVE</span>
                </button>
              </div>
              {onDelete && (
                <button type="button" className="event-form__delete" onClick={() => onDelete(event)}>
                  Delete event
                </button>
              )}
            </>
          ) : (
            <div className="event-form__footer">
              <button type="submit" className="btn btn-primary" disabled={isSaving}>
                <span className="btn-text">CREATE EVENT</span>
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EventModal;
